import { type WorkOrderChangeObjectItem, WorkOrderChangeStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { Button, cnMixSpace, Tooltip } from 'uikit';

import { useCallback, useMemo, useRef, useState } from 'react';

import { WorkOrderChapters } from '../../WorkOrder/workorderTypes';
import { AddOtherToWorkOrderChangeTable } from '../AddOtherToWorkOrderChangeTable';
import type { TFilters } from '../WorkOrderChange';

type TAddOthersProps = {
    isDisabled: boolean;
    constructionTypeId: number;
    workOrderChangeObjectsData: WorkOrderChangeObjectItem[];
    filters: TFilters;
    withBtn?: boolean;
    workOrderChangeStatus?: WorkOrderChangeStatusEnum;
    refreshOthersTable?: () => void;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
} & (
    | {
          isModalOpenedOuter: boolean;
          closeModalOuter: () => void;
      }
    | {
          isModalOpenedOuter?: never;
          closeModalOuter?: never;
      }
);

export const AddOtherToWorkOrderChange = ({
    isDisabled,
    withBtn = false,
    constructionTypeId,
    workOrderChangeObjectsData,
    filters,
    workOrderChangeStatus,
    refreshOthersTable,
    workOrderChangeUerRules,
    closeModalOuter,
    isModalOpenedOuter,
}: TAddOthersProps) => {
    const refButton = useRef<HTMLDivElement>(null);

    const [isOpened, setIsOpened] = useState(false);
    const [isOpenTooltip, setIsOpenTooltip] = useState<boolean>(false);

    const workOrderChangeStatusOnFormation = workOrderChangeStatus === WorkOrderChangeStatusEnum.OnFormation;

    const isFiltersSelected = useMemo(
        () => filters.chapter === WorkOrderChapters.OTHERS && filters.objectId,
        [filters.chapter, filters.objectId],
    );

    const onOpenHandler = useCallback(() => {
        setIsOpened(true);
    }, []);

    const successAddHandler = useCallback(() => {
        refreshOthersTable?.();
        setIsOpened(false);

        if (isModalOpenedOuter) {
            closeModalOuter?.();
        }
    }, [closeModalOuter, isModalOpenedOuter, refreshOthersTable]);

    const cancelAddHandler = useCallback(() => {
        setIsOpened(false);

        if (isModalOpenedOuter) {
            closeModalOuter?.();
        }
    }, [closeModalOuter, isModalOpenedOuter]);

    const handleMouseMoveButton = useCallback(() => {
        if (!isFiltersSelected && workOrderChangeStatusOnFormation) {
            setIsOpenTooltip(true);
        }
    }, [isFiltersSelected, workOrderChangeStatusOnFormation]);

    const handleMouseLeaveButton = useCallback(() => {
        setIsOpenTooltip(false);
    }, []);

    return (
        <>
            {withBtn && (
                <div
                    ref={refButton}
                    onMouseMove={handleMouseMoveButton}
                    onMouseLeave={handleMouseLeaveButton}
                    style={{ display: 'inline-block', marginRight: '10px' }}
                >
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Добавить прочие"
                        view="secondary"
                        size="s"
                        onClick={onOpenHandler}
                        disabled={isDisabled}
                    />
                </div>
            )}
            {isOpenTooltip && (
                <Tooltip size="s" direction="upCenter" anchorRef={refButton} isInteractive={false}>
                    Выберите раздел &quot;Прочие&quot; и объект
                </Tooltip>
            )}
            {(isModalOpenedOuter || isOpened) && (
                <AddOtherToWorkOrderChangeTable
                    constructionTypeId={constructionTypeId}
                    workOrderChangeObjectsData={workOrderChangeObjectsData}
                    handlerSuccess={successAddHandler}
                    handlerClose={cancelAddHandler}
                    workOrderChangeUerRules={workOrderChangeUerRules}
                />
            )}
        </>
    );
};

AddOtherToWorkOrderChange.displayName = 'AddOtherToWorkOrderChange';
